import { nanoid } from "nanoid"
import { jobShopTag, pageTag, tenantTag } from "@/utils/cacheTags"
import Logger from "@/utils/Logger"

export async function usePageData(pageSlug: string) {
  const { $api } = useNuxtApp()
  const { t } = useI18n()
  const { tenantId, jobShopId, acceptLanguage } = useHeaderStore()
  const { dataCacheTtl, dataCacheStaleTtl } = useRuntimeConfig()

  const requestId = nanoid()
  const contextString = `page data for pageSlug: "${pageSlug}", tenantId: "${tenantId}" and jobShopId: "${jobShopId}"`

  let startTime
  if (import.meta.server) {
    Logger(requestId).start(`Start fetching ${contextString}`)
    startTime = Date.now()
  }

  const cacheKey = `pageData-${pageSlug}`
  const {
    data: page,
    status: pageStatus,
    error: pageError,
  } = await useAsyncData(cacheKey, () =>
    // Stale-if-error (JET-2212) on upstream unavailability (not 4xx); tags include the resolved page id.
    useDataCacheWithStale(cacheKey, () => $api.page.getPageBySlug(tenantId, jobShopId, acceptLanguage, pageSlug), {
      maxAge: dataCacheTtl,
      staleIfError: dataCacheStaleTtl,
      requestId,
      tags: data => [tenantTag(tenantId), jobShopTag(jobShopId), pageTag(data.id)],
    }))

  if (import.meta.server) {
    if (!pageError.value) {
      const duration = Date.now() - startTime
      Logger(requestId).success(`Successfully fetched ${contextString}. (${duration}ms)`)
    }
    else {
      const errorMessage = pageError.value?.data?.errors?.[0]?.message || pageError.value?.data?.description || "Unknown error occurred"
      Logger(requestId).error(`Error while fetching ${contextString}: ${errorMessage}\n\n`, pageError.value)
    }
  }

  if (pageError.value) {
    if (pageError.value.statusCode === 404) {
      throw createError({
        statusCode: 404,
        statusMessage: t("error-messages.page-not-found"),
      })
    }
    else {
      throw createError({
        statusCode: pageError.value.statusCode,
        statusMessage: t("error-messages.general"),
      })
    }
  }

  return { page, pageStatus, pageError }
}
